import React, { useState, useRef, useEffect } from 'react';

// Configuration for debug info visibility (modular toggle)
const SHOW_DEBUG_INFO = true; // Set to false to hide debug info completely

// Safe text formatting function - converts markdown-style text to HTML
const formatResponseText = (text) => {
  if (!text || typeof text !== 'string') return text;

  let formatted = text
    // Convert **bold** to <strong>
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    // Convert line breaks to <br>
    .replace(/\n/g, '<br>')
    // Convert → arrows to styled arrows
    .replace(/→/g, '<span class="arrow">→</span>')
    // Convert --- dividers to styled dividers
    .replace(/---/g, '<hr class="response-divider">')
    // Make section headers stand out
    .replace(/\*\*([^:]+):\*\*/g, '<div class="section-header">$1:</div>');

  return formatted;
};

function Chatbot() {
  // UI State
  const [visible, setVisible] = useState(false);
  const [shouldRender, setShouldRender] = useState(false);
  const [input, setInput] = useState('');
  const [hoveredInfoButton, setHoveredInfoButton] = useState(null);
  const [hoveredToggle, setHoveredToggle] = useState(false);

  // Chat State
  const [messages, setMessages] = useState([
    { sender: 'bot', text: 'Welcome to iResponse! How can I assist you?' }
  ]);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestion, setLoadingSuggestion] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Navigation context state
  const [navigationContext, setNavigationContext] = useState({
    level: 'main',
    categoryId: null,
    categoryTitle: null
  });

  const chatRef = useRef(null);

  // Load static categories on mount
  useEffect(() => {
    fetch('https://dev.iresponse.co.in/cb_api/suggestions/static_categories')
      .then(res => res.json())
      .then(data => setSuggestions(data.suggestions || []))
      .catch(() => setSuggestions([]));
  }, []);

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatRef.current) {
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    }
  }, [messages]);

  // Toggle chatbot visibility
  const toggleChat = () => {
    console.log('🔍 Toggle chat clicked. Current state:', { visible, shouldRender });
    
    if (visible) {
      setVisible(false);
      setTimeout(() => setShouldRender(false), 300);
    } else {
      setShouldRender(true);
      setTimeout(() => setVisible(true), 10);
    }
    
    // Debug log after state changes
    setTimeout(() => {
      console.log('🔍 After toggle:', { visible, shouldRender });
    }, 50);
  };

  // Toggle suggestions visibility
  const toggleSuggestions = () => setShowSuggestions(prev => !prev);

  // Reset chat function - clears messages and resets suggestions
  const resetChat = async () => {
    // Reset messages to welcome message
    setMessages([
      { sender: 'bot', text: 'Welcome to iResponse! How can I assist you?' }
    ]);

    // Reset navigation context
    setNavigationContext({
      level: 'main',
      categoryId: null,
      categoryTitle: null
    });

    // Reset input
    setInput('');

    // Reset suggestions to static categories
    try {
      const response = await fetch('https://dev.iresponse.co.in/cb_api/suggestions/static_categories');
      const data = await response.json();
      setSuggestions(data.suggestions || []);
    } catch {
      setSuggestions([]);
    }

    // Reset suggestion visibility
    setShowSuggestions(false);
  };

  // Send message function
  const sendMessage = async (text, metadata = {}, clickedSuggestion = null) => {
    // Add interaction tracking
    const interaction_type = metadata.interaction_type || 'manual';
    const session_id = metadata.session_id || 'default';

    console.log("🔍 PHASE 1 DIAGNOSTIC - sendMessage called:");
    console.log(" - Text:", text);
    console.log(" - Interaction Type:", interaction_type);
    console.log(" - Session ID:", session_id);
    console.log(" - Navigation Context:", navigationContext);
    console.log(" - Timestamp:", new Date().toISOString());

    const userMessage = { sender: 'user', text };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      const response = await fetch('https://dev.iresponse.co.in/cb_api/process_input_enhanced', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: text,
          session_id: session_id,
          interaction_type: interaction_type,
          navigationContext: navigationContext,
          // Forward the clicked suggestion for backend augmentation
          selected_suggestion: clickedSuggestion,
          selectedSuggestion: clickedSuggestion,
          clicked_button: clickedSuggestion,
          button: clickedSuggestion
        })
      });

      const data = await response.json();
      const botText = data.response || 'Sorry, I could not process your request.';

      setMessages(prev => [...prev, {
        sender: 'bot',
        text: botText,
        debug: data.debug_info
      }]);

      // Smart suggestion handling based on backend directive
      if (data.ui_directive?.preserve_suggestions) {
        // Keep current suggestions - don't update
        console.log('🎯 Preserving current suggestions as instructed by backend');
      } else if (data.suggestions && data.suggestions.suggestions) {
        // Update suggestions as normal
        setSuggestions(data.suggestions.suggestions);
      }

    } catch (error) {
      setMessages(prev => [...prev, { sender: 'bot', text: 'Error contacting server.' }]);
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle suggestion clicks
  const handleSuggestionClick = async (suggestion) => {
    setLoadingSuggestion(suggestion.text);

    if (suggestion.action === 'show_subcategories') {
      // Update navigation context when entering subcategories
      setNavigationContext({
        level: 'subcategory',
        categoryId: suggestion.id,
        categoryTitle: suggestion.title || suggestion.text
      });

      try {
        const response = await fetch(`https://dev.iresponse.co.in/cb_api/suggestions/subcategories/${suggestion.id}`);
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } catch {
        console.error('Error loading subcategories');
      }

    } else if (suggestion.action === 'trigger_nlp') {
      // Enhanced subcategory click with context preservation
      console.log("🔍 PHASE 1 DIAGNOSTIC - Quick action triggered:", suggestion.section);

      const text = suggestion.query || suggestion.section;
      await sendMessage(text, {
        interaction_type: 'quick_action',
        session_id: `quick_action_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        quick_action_data: {
          section: suggestion.section,
          action: suggestion.action
        }
      }, suggestion);

    } else if (suggestion.action === 'go_back_to_static') {
      // Reset navigation context when going back
      setNavigationContext({
        level: 'main',
        categoryId: null,
        categoryTitle: null
      });

      try {
        const response = await fetch('https://dev.iresponse.co.in/cb_api/suggestions/static_categories');
        const data = await response.json();
        setSuggestions(data.suggestions || []);
      } catch {
        console.error('Error loading static categories');
      }
    }

    setLoadingSuggestion(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    // PHASE 1 DIAGNOSTIC: Mark as manual input
    console.log("🔍 PHASE 1 DIAGNOSTIC - Manual input submitted:", input);
    sendMessage(input, {
        interaction_type: 'manual',
        session_id: 'default'
    });
    setInput('');
  };

  const closeChat = () => {
    toggleChat();
  };

  const renderSuggestions = () => {
    if (!suggestions || suggestions.length === 0) return null;

    return (
      <div style={{
        padding: '10px',
        borderTop: '1px solid #eee'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px'
        }}>
          <span style={{
            fontWeight: 'bold',
            fontSize: '14px',
            color:'black'
          }}>Quick Actions</span>
          <button 
            onClick={toggleSuggestions} 
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '16px'
            }}
          >
            {showSuggestions ? '▼' : '▲'}
          </button>
        </div>

        {showSuggestions && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '8px'
          }}>
            {suggestions.map((suggestion, index) => (
              <button
                key={index}
                onClick={() => handleSuggestionClick(suggestion)}
                disabled={loadingSuggestion === suggestion.text}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  background: loadingSuggestion === suggestion.text ? '#f0f0f0' : '#fff',
                  cursor: loadingSuggestion === suggestion.text ? 'not-allowed' : 'pointer',
                  fontSize: '12px',
                  textAlign: 'center',
                  transition: 'all 0.2s ease'
                }}
              >
                {loadingSuggestion === suggestion.text ? (
                  <span>Loading...</span>
                ) : (
                  <>
                    {suggestion.icon && <span style={{ marginRight: '4px' }}>{suggestion.icon}</span>}
                    <span>{suggestion.title || suggestion.text}</span>
                  </>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <button 
        onClick={toggleChat}
        onMouseEnter={() => setHoveredToggle(true)}
        onMouseLeave={() => setHoveredToggle(false)}
        style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          width: '60px',
          height: '60px',
          backgroundColor: '#007bff',
          border: 'none',
          borderRadius: '50%',
          color: 'white',
          fontSize: '24px',
          cursor: 'pointer',
          boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
          zIndex: 10001,
          transition: 'all 0.3s ease',
          transform: hoveredToggle ? 'scale(1.1)' : 'scale(1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {visible ? '×' : '💬'}
      </button>

      {/* Chat Interface */}
      {shouldRender && (
        <div style={{
          position: 'fixed',
          bottom: '90px',
          right: '20px',
          width: '350px',
          height: '500px',
          background: 'white',
          borderRadius: '10px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(20px)',
          transition: 'all 0.3s ease',
          border: '1px solid #e0e0e0'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '15px',
            background: '#007bff',
            color: 'white',
            borderRadius: '10px 10px 0 0'
          }}>
            <div>
              <h3 style={{
                margin: '0 0 5px 0',
                fontSize: '16px'
              }}>iResponse Support</h3>
              <span style={{
                fontSize: '12px',
                opacity: '0.9'
              }}>Bihar Disaster Management Assistant</span>
            </div>
            <div>
              <button 
                onClick={resetChat} 
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '18px',
                  cursor: 'pointer',
                  marginRight: '10px',
                  padding: '5px'
                }}
                title="Reset Chat"
              >
                🔄
              </button>
              <button 
                onClick={closeChat} 
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'white',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '5px'
                }}
                title="Close Chat"
              >
                ×
              </button>
            </div>
          </div>

          <div 
            ref={chatRef}
            style={{
              flex: 1,
              overflowY: 'auto',
              padding: '10px'
            }}
          >
            {messages.map((msg, index) => (
              <div 
                key={index} 
                style={{
                  marginBottom: '15px',
                  display: 'flex',
                  justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
                }}
              >
                <div style={{
                  maxWidth: '80%',
                  position: 'relative'
                }}>
                  <div style={{
                    padding: '10px 12px',
                    borderRadius: '12px',
                    backgroundColor: msg.sender === 'user' ? '#007bff' : '#f1f1f1',
                    color: msg.sender === 'user' ? 'white' : '#333',
                    fontSize: '14px',
                    lineHeight: '1.4',
                    wordBreak: 'break-word'
                  }}>
                    <div dangerouslySetInnerHTML={{ __html: formatResponseText(msg.text) }} />
                  </div>
                  
                  {/* Debug info button with inline tooltip */}
                  {/* {msg.debug && SHOW_DEBUG_INFO && (
                    <button 
                      type="button"
                      onMouseEnter={() => setHoveredInfoButton(index)}
                      onMouseLeave={() => setHoveredInfoButton(null)}
                      style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        backgroundColor: '#007bff',
                        color: 'white',
                        border: 'none',
                        fontSize: '10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      i
                      {hoveredInfoButton === index && (
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: 'calc(100% + 10px)',
                          transform: 'translateY(-50%)',
                          backgroundColor: '#333',
                          color: '#fff',
                          whiteSpace: 'pre-wrap',
                          padding: '8px 12px',
                          borderRadius: '6px',
                          fontSize: '12px',
                          lineHeight: '1.4',
                          width: '180px',
                          zIndex: 10,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
                        }}>
                          {`Tier: ${msg.debug.tier}\nConf: ${(msg.debug.confidence * 100).toFixed(1)}%\nTime: ${(msg.debug.response_time * 1000).toFixed(0)}ms`}
                          <div style={{
                            position: 'absolute',
                            top: '50%',
                            left: '-5px',
                            transform: 'translateY(-50%)',
                            width: '0',
                            height: '0',
                            borderTop: '5px solid transparent',
                            borderBottom: '5px solid transparent',
                            borderRight: '5px solid #333'
                          }}></div>
                        </div>
                      )}
                    </button>
                  )} */}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {isLoading && (
              <div style={{
                marginBottom: '15px',
                display: 'flex',
                justifyContent: 'flex-start'
              }}>
                <div style={{
                  padding: '10px 12px',
                  borderRadius: '12px',
                  backgroundColor: '#f1f1f1',
                  color: '#333'
                }}>
                  <span style={{
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}>
                    <span style={{
                      opacity: '0',
                      animation: 'loading-fade 1.2s infinite 0s'
                    }}>.</span>
                    <span style={{
                      opacity: '0',
                      animation: 'loading-fade 1.2s infinite 0.2s'
                    }}>.</span>
                    <span style={{
                      opacity: '0',
                      animation: 'loading-fade 1.2s infinite 0.4s'
                    }}>.</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {renderSuggestions()}

          <form 
            onSubmit={handleSubmit} 
            style={{
              display: 'flex',
              padding: '10px',
              borderTop: '1px solid #eee',
              gap: '8px'
            }}
          >
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about disaster management..."
              style={{
                flex: 1,
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px',
                outline: 'none'
              }}
            />
            <button 
              type="submit"
              style={{
                padding: '10px 16px',
                background: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Send
            </button>
          </form>
        </div>
      )}

      {/* CSS Animation keyframes */}
      <style jsx>{`
        @keyframes loading-fade {
          0%, 100% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}

export default Chatbot;


// import React, { useState, useRef, useEffect } from 'react';

// // Configuration for debug info visibility (modular toggle)
// const SHOW_DEBUG_INFO = true; // Set to false to hide debug info completely

// // Safe text formatting function - converts markdown-style text to HTML
// const formatResponseText = (text) => {
//   if (!text || typeof text !== 'string') return text;

//   let formatted = text
//     // Convert **bold** to <strong>
//     .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
//     // Convert line breaks to <br>
//     .replace(/\n/g, '<br>')
//     // Convert → arrows to styled arrows
//     .replace(/→/g, '<span class="arrow">→</span>')
//     // Convert --- dividers to styled dividers
//     .replace(/---/g, '<hr class="response-divider">')
//     // Make section headers stand out
//     .replace(/\*\*([^:]+):\*\*/g, '<div class="section-header">$1:</div>');

//   return formatted;
// };

// function Chatbot({
//   visible, shouldRender, messages, suggestions, showSuggestions,
//   loadingSuggestion, isLoading, chatRef, toggleChat, toggleSuggestions,
//   sendMessage, handleSuggestionClick
// }) {
//   const [input, setInput] = useState('');
//   const [hoveredInfoButton, setHoveredInfoButton] = useState(null);
//   const [hoveredToggle, setHoveredToggle] = useState(false);

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     if (!input.trim()) return;

//     // PHASE 1 DIAGNOSTIC: Mark as manual input
//     console.log("🔍 PHASE 1 DIAGNOSTIC - Manual input submitted:", input);
//     sendMessage(input, {
//         interaction_type: 'manual',
//         session_id: 'default'
//     });
//     setInput('');
//   };

//   const closeChat = () => {
//     toggleChat();
//   };

//   // Reset functionality - clears messages and resets to welcome state
//   const handleReset = () => {
//     // Clear input field
//     setInput('');

//     // If parent component has reset method, call it
//     if (window.resetChat) {
//       window.resetChat();
//     }
//   };

//   const renderSuggestions = () => {
//     if (!suggestions || suggestions.length === 0) return null;

//     return (
//       <div style={{
//         padding: '10px',
//         borderTop: '1px solid #eee'
//       }}>
//         <div style={{
//           display: 'flex',
//           justifyContent: 'space-between',
//           alignItems: 'center',
//           marginBottom: '10px'
//         }}>
//           <span style={{
//             fontWeight: 'bold',
//             fontSize: '14px'
//           }}>Quick Actions</span>
//           <button 
//             onClick={toggleSuggestions} 
//             style={{
//               background: 'none',
//               border: 'none',
//               cursor: 'pointer',
//               fontSize: '16px'
//             }}
//           >
//             {showSuggestions ? '▼' : '▲'}
//           </button>
//         </div>

//         {showSuggestions && (
//           <div style={{
//             display: 'grid',
//             gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
//             gap: '8px'
//           }}>
//             {suggestions.map((suggestion, index) => (
//               <button
//                 key={index}
//                 onClick={() => handleSuggestionClick(suggestion)}
//                 disabled={loadingSuggestion === suggestion.text}
//                 style={{
//                   padding: '8px 12px',
//                   border: '1px solid #ddd',
//                   borderRadius: '6px',
//                   background: loadingSuggestion === suggestion.text ? '#f0f0f0' : '#fff',
//                   cursor: loadingSuggestion === suggestion.text ? 'not-allowed' : 'pointer',
//                   fontSize: '12px',
//                   textAlign: 'center',
//                   transition: 'all 0.2s ease'
//                 }}
//               >
//                 {loadingSuggestion === suggestion.text ? (
//                   <span>Loading...</span>
//                 ) : (
//                   <>
//                     {suggestion.icon && <span style={{ marginRight: '4px' }}>{suggestion.icon}</span>}
//                     <span>{suggestion.title || suggestion.text}</span>
//                   </>
//                 )}
//               </button>
//             ))}
//           </div>
//         )}
//       </div>
//     );
//   };

//   return (
//     <>
//       {/* Chat Toggle Button */}
//       <button 
//         onClick={toggleChat}
//         onMouseEnter={() => setHoveredToggle(true)}
//         onMouseLeave={() => setHoveredToggle(false)}
//         style={{
//           position: 'fixed',
//           bottom: '20px',
//           right: '20px',
//           width: '60px',
//           height: '60px',
//           backgroundColor: '#007bff',
//           border: 'none',
//           borderRadius: '50%',
//           color: 'white',
//           fontSize: '24px',
//           cursor: 'pointer',
//           boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
//          zIndex: 10001,
//           transition: 'all 0.3s ease',
//           transform: hoveredToggle ? 'scale(1.1)' : 'scale(1)',
//           display: 'flex',
//           alignItems: 'center',
//           justifyContent: 'center'
//         }}
//       >
//         💬
//       </button>

//       {/* Chat Interface */}
//       {shouldRender && (
//         <div style={{
//           position: 'fixed',
//           bottom: '90px',
//           right: '20px',
//           width: '350px',
//           height: '500px',
//           background: 'white',
//           borderRadius: '10px',
//           boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
//           zIndex: 10000,
//           display: 'flex',
//           flexDirection: 'column',
//           opacity: visible ? 1 : 0,
//           transform: visible ? 'translateY(0)' : 'translateY(20px)',
//           transition: 'all 0.3s ease',
//           border: '1px solid #e0e0e0'
//         }}>
//           <div style={{
//             display: 'flex',
//             justifyContent: 'space-between',
//             alignItems: 'center',
//             padding: '15px',
//             background: '#007bff',
//             color: 'white',
//             borderRadius: '10px 10px 0 0'
//           }}>
//             <div>
//               <h3 style={{
//                 margin: '0 0 5px 0',
//                 fontSize: '16px'
//               }}>iResponse Support</h3>
//               <span style={{
//                 fontSize: '12px',
//                 opacity: '0.9'
//               }}>Bihar Disaster Management Assistant</span>
//             </div>
//             <div>
//               <button 
//                 onClick={handleReset} 
//                 style={{
//                   background: 'none',
//                   border: 'none',
//                   color: 'white',
//                   fontSize: '18px',
//                   cursor: 'pointer',
//                   marginRight: '10px',
//                   padding: '5px'
//                 }}
//                 title="Reset Chat"
//               >
//                 🔄
//               </button>
//               <button 
//                 onClick={closeChat} 
//                 style={{
//                   background: 'none',
//                   border: 'none',
//                   color: 'white',
//                   fontSize: '20px',
//                   cursor: 'pointer',
//                   padding: '5px'
//                 }}
//                 title="Close Chat"
//               >
//                 ×
//               </button>
//             </div>
//           </div>

//           <div 
//             ref={chatRef}
//             style={{
//               flex: 1,
//               overflowY: 'auto',
//               padding: '10px'
//             }}
//           >
//             {messages.map((msg, index) => (
//               <div 
//                 key={index} 
//                 style={{
//                   marginBottom: '15px',
//                   display: 'flex',
//                   justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start'
//                 }}
//               >
//                 <div style={{
//                   maxWidth: '80%',
//                   position: 'relative'
//                 }}>
//                   <div style={{
//                     padding: '10px 12px',
//                     borderRadius: '12px',
//                     backgroundColor: msg.sender === 'user' ? '#007bff' : '#f1f1f1',
//                     color: msg.sender === 'user' ? 'white' : '#333',
//                     fontSize: '14px',
//                     lineHeight: '1.4',
//                     wordBreak: 'break-word'
//                   }}>
//                     <div dangerouslySetInnerHTML={{ __html: formatResponseText(msg.text) }} />
//                   </div>
                  
//                   {/* Debug info button with inline tooltip */}
//                   {/* {msg.debug && SHOW_DEBUG_INFO && (
//                     <button 
//                       type="button"
//                       onMouseEnter={() => setHoveredInfoButton(index)}
//                       onMouseLeave={() => setHoveredInfoButton(null)}
//                       style={{
//                         position: 'absolute',
//                         top: '-5px',
//                         right: '-5px',
//                         width: '20px',
//                         height: '20px',
//                         borderRadius: '50%',
//                         backgroundColor: '#007bff',
//                         color: 'white',
//                         border: 'none',
//                         fontSize: '10px',
//                         cursor: 'pointer',
//                         display: 'flex',
//                         alignItems: 'center',
//                         justifyContent: 'center'
//                       }}
//                     >
//                       i
//                       {hoveredInfoButton === index && (
//                         <div style={{
//                           position: 'absolute',
//                           top: '50%',
//                           left: 'calc(100% + 10px)',
//                           transform: 'translateY(-50%)',
//                           backgroundColor: '#333',
//                           color: '#fff',
//                           whiteSpace: 'pre-wrap',
//                           padding: '8px 12px',
//                           borderRadius: '6px',
//                           fontSize: '12px',
//                           lineHeight: '1.4',
//                           width: '180px',
//                           zIndex: 10,
//                           boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
//                         }}>
//                           {`Tier: ${msg.debug.tier}\nConf: ${(msg.debug.confidence * 100).toFixed(1)}%\nTime: ${(msg.debug.response_time * 1000).toFixed(0)}ms`}
//                           <div style={{
//                             position: 'absolute',
//                             top: '50%',
//                             left: '-5px',
//                             transform: 'translateY(-50%)',
//                             width: '0',
//                             height: '0',
//                             borderTop: '5px solid transparent',
//                             borderBottom: '5px solid transparent',
//                             borderRight: '5px solid #333'
//                           }}></div>
//                         </div>
//                       )}
//                     </button>
//                   )} */}
//                 </div>
//               </div>
//             ))}

//             {/* Loading indicator */}
//             {isLoading && (
//               <div style={{
//                 marginBottom: '15px',
//                 display: 'flex',
//                 justifyContent: 'flex-start'
//               }}>
//                 <div style={{
//                   padding: '10px 12px',
//                   borderRadius: '12px',
//                   backgroundColor: '#f1f1f1',
//                   color: '#333'
//                 }}>
//                   <span style={{
//                     display: 'inline-flex',
//                     alignItems: 'center'
//                   }}>
//                     <span style={{
//                       opacity: '0',
//                       animation: 'loading-fade 1.2s infinite 0s'
//                     }}>.</span>
//                     <span style={{
//                       opacity: '0',
//                       animation: 'loading-fade 1.2s infinite 0.2s'
//                     }}>.</span>
//                     <span style={{
//                       opacity: '0',
//                       animation: 'loading-fade 1.2s infinite 0.4s'
//                     }}>.</span>
//                   </span>
//                 </div>
//               </div>
//             )}
//           </div>

//           {renderSuggestions()}

//           <form 
//             onSubmit={handleSubmit} 
//             style={{
//               display: 'flex',
//               padding: '10px',
//               borderTop: '1px solid #eee',
//               gap: '8px'
//             }}
//           >
//             <input
//               type="text"
//               value={input}
//               onChange={(e) => setInput(e.target.value)}
//               placeholder="Ask about disaster management..."
//               style={{
//                 flex: 1,
//                 padding: '10px',
//                 border: '1px solid #ddd',
//                 borderRadius: '6px',
//                 fontSize: '14px',
//                 outline: 'none'
//               }}
//             />
//             <button 
//               type="submit"
//               style={{
//                 padding: '10px 16px',
//                 background: '#007bff',
//                 color: 'white',
//                 border: 'none',
//                 borderRadius: '6px',
//                 cursor: 'pointer',
//                 fontSize: '14px',
//                 fontWeight: '500'
//               }}
//             >
//               Send
//             </button>
//           </form>
//         </div>
//       )}

//       {/* CSS Animation keyframes - need to be added to a style tag */}
//       <style jsx>{`
//         @keyframes loading-fade {
//           0%, 100% {
//             opacity: 0;
//           }
//           50% {
//             opacity: 1;
//           }
//         }
//       `}</style>
//     </>
//   );
// }

// export default Chatbot;